import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { raygunHttpError } from '@approot/app.raygun.setup';
import {
  ExcludedTradeStatusesKey,
  KeyValue,
  SubAccount,
  Trade
} from '@approot/models';
import { Header } from '@approot/models/ag-grid.model';
import {
  CreateAllocationResponse,
  DepositTakerAndCurrencies,
  DvEntities,
  Entity,
  StrictAllocations
} from '@approot/models/trade-capture';
import { WhiteLabelBlotterResponse } from '@approot/models/white-label-blotter';
import { AlertsService } from '@approot/services/alerts/alerts.service';
import { Utilities, apiURL } from '@approot/utilities';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/auth.service';
import { UserStorageService } from '../user-storage/user-storage.service';

export interface CreateAllocationParametersPayload {
  task: 'createAllocationParameters';
  item: Trade;
  allocationsAttribute: 'notional' | 'counterNotional';
  allocations: KeyValue;
  subAccountsAllocationKey: string;
  subAccounts: SubAccount[];
  splitMethod: 'Proportional';
  splitRelatedTrades: boolean;
  newCounterPartyB?: string;
  newCounterPartyA?: string;
}

interface SubAccountsResponse {
  response: {
    eligibleAllocationKeys: string[];
    entity: string;
    subAccounts: SubAccount[];
    strictAllocations: StrictAllocations;
  };
  status: string;
}

interface GetUserInfoTradeCapturePayLoad {
  table?: string;
  data?: any;
  key?: string;
  primaryKey?: string;
  s3Bucket?: string;
  filename?: string;
  deleteFile?: string;
  task?: Task;
  identifier?: string;
  reportCreationTime?: string;
  item?: any;
  tradeId?: string;
  tradeIds?: string[];
  medusaEnvironment?: string;
  excludeSchedules?: boolean;
  date?: string;
  time?: string;
  timezone?: string;
  excludedStatuses?: string[];
}

type Task =
  | 'updateMedusaPassword'
  | 'getAll'
  | 'getS3Object'
  | 'getTrades'
  | 'saveTrade'
  | 'deleteTrade'
  | 'putItem'
  | 'loadReportDateTimes'
  | 'loadReport'
  | 'treasuryCurve:write'
  | 'treasuryCurve:read'
  | 'treasuryCurve:delete'
  | 'getItemWithUniqueIdentifier'
  | 'saveUserScheduledReports'
  | 'loadUserScheduledReports'
  | 'getMedusaCredentials'
  | 'deleteItem'
  | 'archiveTrade'
  | 'loadTradeRecords'
  | 'getAvailableDepositTakersAndCurrencies'
  | 'isExistingTradeId'
  | 'getTradesByTradeIds'
  | 'getAlerts'
  | 'setAlerts'
  | 'getTradeStatuses';

@Injectable({
  providedIn: 'root'
})
export class TradesCaptureService {
  public displayTradeViewButton = false;
  public depositTakersAndCurrencies: DepositTakerAndCurrencies[] = [];
  private _depositTakersAndCurrenciesSubject = new BehaviorSubject<
    DepositTakerAndCurrencies[]
  >([]);
  public readonly depositTakersAndCurrenciesSubject =
    this._depositTakersAndCurrenciesSubject.asObservable();
  private _showEntities = false;
  private _showCreditCurves = false;
  private _showCsa = false;
  private _showTreasuryCurves = false;
  private _dvEntities: DvEntities;
  private _tradeAttributes: any;
  private _showScheduleReport = false;
  private _showAlert = false;
  private _tradesGridHeaders: Header[] = [];
  private tradesDoneSubject = new Subject<null>();
  public readonly tradeDone$ = this.tradesDoneSubject.asObservable();

  private _tradeCaptureTablesLoaded = new BehaviorSubject<boolean>(false);
  tradeCaptureTablesLoaded = this._tradeCaptureTablesLoaded.asObservable();
  private _entities: Entity[] = [];

  private _trades = new BehaviorSubject<KeyValue[]>([]);
  private dataStore: { trades: KeyValue[] } = { trades: [] };
  // this is the main observable that the trades grid watches
  readonly trades = this._trades.asObservable();
  private _allTradeCaptureDataLoaded = new BehaviorSubject<boolean>(false);
  readonly allTradeCaptureDataLoaded =
    this._allTradeCaptureDataLoaded.asObservable();

  private _agGridHeader: any[] = [];

  constructor(
    private http: HttpClient,
    private alertsService: AlertsService,
    private authService: AuthService,
    private userStorageService: UserStorageService
  ) {}

  get savedTrades(): any {
    return Object.assign({}, this.dataStore).trades;
  }

  get showEntities(): boolean {
    return this._showEntities;
  }

  get showCreditCurves(): boolean {
    return this._showCreditCurves;
  }

  get showCsa(): boolean {
    return this._showCsa;
  }

  get dvEntities(): DvEntities {
    return this._dvEntities;
  }

  get tradeAttributes() {
    return this._tradeAttributes;
  }

  get showTreasuryCurves() {
    return this._showTreasuryCurves;
  }

  get showScheduleReports() {
    return this._showScheduleReport;
  }

  get showAlerts() {
    return this._showAlert;
  }

  set setShowEntities(value: boolean) {
    this._showEntities = value;
  }

  set setShowCreditCurves(value: boolean) {
    this._showCreditCurves = value;
  }

  set setShowCsa(value: boolean) {
    this._showCsa = value;
  }

  set setShowScheduleReports(value: boolean) {
    this._showScheduleReport = value;
  }

  set setShowAlerts(value: boolean) {
    this._showAlert = value;
  }

  set setShowTreasuryCurves(value: boolean) {
    this._showTreasuryCurves = value;
  }

  set setDvEntities(dvEntities: DvEntities) {
    this._dvEntities = dvEntities;
  }

  set setTradeAttributes(attributes: any) {
    this._tradeAttributes = attributes;
  }

  set entities(entities: Entity[]) {
    this._entities = entities;
  }

  public setDepositTakersAndCurrencies(
    depositTakersAndCurrencies: DepositTakerAndCurrencies[]
  ): void {
    this.depositTakersAndCurrencies = depositTakersAndCurrencies;
    this._depositTakersAndCurrenciesSubject.next(depositTakersAndCurrencies);
  }

  set setAgGridHeader(header) {
    this._agGridHeader = header;
  }

  get agGridHeader() {
    return this._agGridHeader;
  }

  allTradeRelatedDataLoaded() {
    this._allTradeCaptureDataLoaded.next(true);
  }

  get entities(): Entity[] {
    return this._entities;
  }

  set setTradeGridHeaders(headers: Header[]) {
    this._tradesGridHeaders = headers;
  }

  get tradesGridHeaders() {
    return this._tradesGridHeaders;
  }

  setTradeGridHeaderIndex(index: number, header: Header) {
    this.tradesGridHeaders[index] = header;
  }

  public getTrades(hide403Errors = false, excludeSchedules = false) {
    let timezone = Utilities.currentTimezone;
    const excludedStatuses = this.userStorageService.getItem<string[]>(
      ExcludedTradeStatusesKey
    );

    return this.getUserInfoRequest(
      { task: 'getTrades', excludeSchedules, timezone, excludedStatuses },
      hide403Errors
    ).pipe(
      tap((trades: any[]) => {
        this.dataStore.trades = trades;
        this._trades.next(Object.assign({}, this.dataStore).trades);
      })
    );
  }

  public getTradesByTradeIds(
    tradeIds: string[],
    date?: string,
    time?: string,
    timezone?: string
  ): Observable<any[]> {
    return this.getUserInfoRequest({
      task: 'getTradesByTradeIds',
      tradeIds,
      date,
      time,
      timezone
    });
  }

  public addRemoveLocalTrade(task: Task, tradeId: string, item?: KeyValue) {
    if (
      task === 'putItem' ||
      task === 'deleteItem' ||
      task === 'saveTrade' ||
      task === 'deleteTrade'
    ) {
      const tradeIndex = this.dataStore.trades.findIndex(
        trade => trade.tradeId === tradeId
      );
      if (tradeIndex !== -1) {
        this.dataStore.trades.splice(tradeIndex, 1); // remove trade from local table if it already exists
      }
      if (task === 'putItem' || task === 'saveTrade') {
        this.dataStore.trades.push(item);
      }
      this._trades.next(Object.assign({}, this.dataStore).trades);
    }
  }

  public getLifeCycledEntities(): Observable<Entity[]> {
    return this.getEntities({ task: 'getLifeCycledEntities' });
  }

  public getEntities(
    options: { task?: string; raw?: boolean } = { raw: false }
  ): Observable<Entity[]> {
    if (typeof options === 'string') {
      options = { task: options };
    }

    options = {
      task: 'getEntities',
      ...options
    };
    const { task, raw } = options;

    // NOTE: ensure the URL contains "fx/get-user-info?task=getEntities"
    // As this is used to detect this call to redact the response from Raygun
    return this.authService
      .fxPost<Entity[]>({ task }, 'get-user-info', `task=${task}`)
      .pipe(
        catchError(this.handleError.bind(this)),
        map((entities: Entity[]) => {
          if (raw) {
            return entities;
          }
          entities = entities.map((e: any) => {
            const ret = {
              ...e,
              identifier: e.shortDisplayName || e.displayName || e.identifier
            };
            delete ret.displayName;
            delete ret.shortDisplayName;
            return ret;
          });
          return entities;
        })
      );
  }

  public setEntity(item: Entity): Observable<any> {
    const task = 'setEntity';
    return this.authService
      .fxPost<any>({ task, item }, 'get-user-info', `task=${task}`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  public deleteEntity(identifier: string): Observable<any> {
    const task = 'deleteEntity';
    return this.authService
      .fxPost<any>({ task, identifier }, 'get-user-info', `task=${task}`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getUserInfoRequest<T>(
    payload: GetUserInfoTradeCapturePayLoad,
    hideErrorMsgOn403 = false
  ): Observable<T> {
    if (payload.table) {
      payload.table =
        this.tableToGet(payload.table) + Utilities.currentEnvironment();
    }

    return this.authService.currentSession$().pipe(
      switchMap(amazonCognito => {
        const params = [];
        ['task', 'table'].forEach(field => {
          if (payload[field]) {
            params.push(`${field}=${payload[field]}`);
          }
        });
        const paramStr = params.join('&');
        return this.http.post<T>(
          'https://' +
            environment.api_domain +
            `.fxexotics.com/fx/get-user-info?${paramStr}`,
          payload,
          this.getHeaders(amazonCognito.getIdToken().getJwtToken())
        );
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        // tslint:disable-next-line: no-console
        console.trace(errorResponse);
        raygunHttpError(errorResponse, ['getUserInfoRequest']);
        if (errorResponse?.error?.msg === 'Item not found') {
          // if the data is not found
          return throwError('notFound');
        } else if (!(hideErrorMsgOn403 && errorResponse.status === 403)) {
          this.handleError(errorResponse);
        }

        return throwError(errorResponse);
      })
    );
  }

  public getCounterPartyList(
    side: 'A' | 'B',
    counterParty?: string
  ): Observable<string[]> {
    const [task, label] =
      side === 'A'
        ? ['getCounterPartyAList', 'counterPartyB']
        : ['getCounterPartyBList', 'counterPartyA'];
    const payload = { task, [label]: counterParty };

    return this.authService
      .fxPost<string[]>(payload, 'get-user-info', `task=${task}`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  tableToGet(tableName: string) {
    switch (tableName) {
      case 'csa':
        return this.dvEntities.csaDataBaseRootName;
      case 'creditCurves':
        return this.dvEntities.creditCurvesDataBaseRootName;
      case 'config':
        return this.dvEntities.configDataBaseRootName;
      // deprecated
      // case 'alerts':
      //   return this.dvEntities.alertDataBaseRootName;
      case 'tradeArchives':
        return this.dvEntities.tradeArchivesDataBaseRootName;
      default:
        return null;
    }
  }

  tradeIdExistChecker(tradeId: string): Observable<{ tradeIdExists: boolean }> {
    return this.getUserInfoRequest({
      task: 'isExistingTradeId',
      tradeId: tradeId.toString()
    });
  }

  runReportNow(payload: any): Observable<any> {
    return this.authService.currentSession$().pipe(
      switchMap(cognitoToken => {
        return this.http
          .post(
            'https://' +
              environment.api_domain +
              '.fxexotics.com/fx/daily-report',
            payload,
            this.getHeaders(cognitoToken.getIdToken().getJwtToken())
          )
          .pipe(catchError(err => this.handleError(err)));
      })
    );
  }

  public createAllocationParameters(
    payload: CreateAllocationParametersPayload
  ): Observable<CreateAllocationResponse> {
    return this.authService.currentSession$().pipe(
      switchMap(cognitoToken => {
        return this.http
          .post<CreateAllocationResponse>(
            'https://' +
              environment.api_domain +
              `.fxexotics.com/fx/get-user-info?${payload.task}`,
            payload,
            this.getHeaders(cognitoToken.getIdToken().getJwtToken())
          )
          .pipe(catchError(err => this.handleError(err)));
      })
    );
  }

  public saveAllocationParameters(payload: {
    task: 'createAndSaveTradesFromAllocationParameters';
    subAccounts: SubAccount[];
    subAccountsAllocationKey: string;
    allocationParameters: CreateAllocationResponse;
  }): Observable<{}> {
    return this.authService.currentSession$().pipe(
      switchMap(cognitoToken => {
        return this.http
          .post<{}>(
            'https://' +
              environment.api_domain +
              `.fxexotics.com/fx/get-user-info?${payload.task}`,
            payload,
            this.getHeaders(cognitoToken.getIdToken().getJwtToken())
          )
          .pipe(
            tap(() => console.log('made a report schedule for now')),
            catchError(err => this.handleError(err))
          );
      })
    );
  }

  public allocationsAvailableSubAccounts(payload: {
    entity: string;
  }): Observable<SubAccountsResponse> {
    return this.authService.currentSession$().pipe(
      switchMap(amazonCognito => {
        return this.http
          .post<SubAccountsResponse>(
            apiURL('/fx/allocationsAvailableSubAccounts'),
            payload,
            this.getHeaders(amazonCognito.getIdToken().getJwtToken())
          )
          .pipe(catchError(err => this.handleError(err)));
      })
    );
  }

  public getRequestsStatus(
    date: string
  ): Observable<WhiteLabelBlotterResponse> {
    const payload = {
      task: 'getRequestsStatus',
      date
    };
    return this.authService.fxPost<WhiteLabelBlotterResponse>(
      payload,
      `whiteLabelServices`,
      `task=${payload.task}`
    );
  }

  public getTradeStatuses(): Observable<any> {
    return this.getUserInfoRequest({ task: 'getTradeStatuses' });
  }

  private handleError(error: HttpErrorResponse) {
    this.alertsService.addHttpAlert({ error });
    // return an observable with a user-facing error message
    return throwError(
      () => new Error('Something bad happened; please try again later.')
    );
  }

  private getHeaders(token) {
    let headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', token);
    return { headers };
  }

  public tradeDone() {
    this.tradesDoneSubject.next(null);
  }
}
